import React, { useContext, useEffect, useRef, useState } from 'react';
import { FaChevronDown, FaLightbulb, FaPaperPlane } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { AuthContext } from '@/AuthContext';
import { strategyDisplayName } from '@/beginnerEducation';

const TradeExplanation = ({ proposalId, fillId, botId, symbol, compact = false }) => {
  const { axiosInstance } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [explanation, setExplanation] = useState(null);
  const [question, setQuestion] = useState('');
  const [conversation, setConversation] = useState([]);
  const [starterQuestionsOpen, setStarterQuestionsOpen] = useState(true);
  const [asking, setAsking] = useState(false);
  const [askError, setAskError] = useState('');
  const [askFailure, setAskFailure] = useState(null);
  const [retryRemaining, setRetryRemaining] = useState(0);
  const askingRef = useRef(false);
  const conversationPanelRef = useRef(null);
  const previousConversationLengthRef = useRef(0);
  const [comparisonOpen, setComparisonOpen] = useState(false);
  const [comparisonOptions, setComparisonOptions] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [comparisonError, setComparisonError] = useState('');
  const [comparing, setComparing] = useState(false);
  // Keep the user-selected, server-registered definition ID.  A comparison
  // result is presentation data and may intentionally omit internal
  // references; derivation still re-authorizes this ID server-side.
  const [selectedComparisonId, setSelectedComparisonId] = useState(null);

  const endpoint = proposalId
    ? `/api/practice/proposals/${proposalId}/explanation`
    : fillId
      ? `/api/practice/fills/${fillId}/explanation`
      : `/api/practice/positions/${botId}/${encodeURIComponent(symbol)}/explanation`;
  const comparisonRoot = proposalId
    ? `/api/practice/proposals/${proposalId}`
    : fillId
      ? `/api/practice/fills/${fillId}`
      : null;
  const askEndpoint = proposalId
    ? `/api/practice/proposals/${proposalId}/ask`
    : fillId
      ? `/api/practice/fills/${fillId}/ask`
      : `/api/practice/positions/${botId}/${encodeURIComponent(symbol)}/ask`;

  useEffect(() => {
    setOpen(false);
    setLoading(false);
    setError('');
    setExplanation(null);
    setQuestion('');
    setConversation([]);
    setStarterQuestionsOpen(true);
    setAsking(false);
    setAskError('');
    setAskFailure(null);
    setRetryRemaining(0);
    askingRef.current = false;
    previousConversationLengthRef.current = 0;
    setComparisonOpen(false);
    setComparisonOptions(null);
    setComparison(null);
    setComparisonError('');
    setComparing(false);
    setSelectedComparisonId(null);
  }, [endpoint]);

  useEffect(() => {
    const previousLength = previousConversationLengthRef.current;
    const latestTurn = conversation[conversation.length - 1];
    if (conversation.length > previousLength && latestTurn?.role === 'assistant') {
      // A submitted question does not change this list while the request is in
      // flight. Scroll only after a completed response, so reading position is
      // not disrupted while the user waits.
      window.requestAnimationFrame(() => {
        const panel = conversationPanelRef.current;
        if (panel) panel.scrollTop = panel.scrollHeight;
      });
    }
    previousConversationLengthRef.current = conversation.length;
  }, [conversation]);

  useEffect(() => {
    if (!askFailure?.retry_after_seconds) return undefined;
    const retryAt = Date.now() + (askFailure.retry_after_seconds * 1000);
    const update = () => setRetryRemaining(Math.max(0, Math.ceil((retryAt - Date.now()) / 1000)));
    update();
    const timer = window.setInterval(update, 1000);
    return () => window.clearInterval(timer);
  }, [askFailure]);

  const load = async () => {
    if (explanation || loading) return;
    setLoading(true);
    setError('');
    try {
      const response = await axiosInstance.get(endpoint, { timeout: 15000 });
      setExplanation(response.data?.explanation || null);
    } catch (requestError) {
      const detail = requestError?.response?.data?.detail;
      setError((typeof detail === 'string' ? detail : detail?.message) || 'Pinngro could not load the recorded explanation.');
    } finally {
      setLoading(false);
    }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) load();
  };

  const loadComparisonOptions = async () => {
    if (!comparisonRoot || comparisonOptions || comparing) return;
    setComparing(true);
    setComparisonError('');
    try {
      const response = await axiosInstance.get(`${comparisonRoot}/strategy-comparison-options`, { timeout: 15000 });
      setComparisonOptions(response.data || { options: [] });
    } catch (requestError) {
      const detail = requestError?.response?.data?.detail;
      setComparisonError((typeof detail === 'string' ? detail : detail?.message) || "Pinngro could not load compatible recorded strategies.");
    } finally {
      setComparing(false);
    }
  };

  const toggleComparison = () => {
    const next = !comparisonOpen;
    setComparisonOpen(next);
    if (next) loadComparisonOptions();
  };

  const compareStrategy = async (strategyDefinitionId) => {
    if (!comparisonRoot || !strategyDefinitionId || comparing) return;
    setComparing(true);
    setComparisonError('');
    setComparison(null);
    setSelectedComparisonId(strategyDefinitionId);
    try {
      const response = await axiosInstance.post(`${comparisonRoot}/strategy-comparison`, {
        strategy_definition_id: strategyDefinitionId,
      }, { timeout: 15000 });
      setComparison(response.data || null);
    } catch (requestError) {
      const detail = requestError?.response?.data?.detail;
      setComparisonError((typeof detail === 'string' ? detail : detail?.message) || "Pinngro could not compare the recorded strategy input.");
    } finally {
      setComparing(false);
    }
  };

  const ask = async (nextQuestion = question, preservedPriorTurns = null) => {
    const submittedQuestion = String(nextQuestion || '').trim();
    if (!submittedQuestion || askingRef.current || !explanation) return;
    const priorTurns = preservedPriorTurns || conversation.slice(-4).map(({ role, content }) => ({ role, content }));
    askingRef.current = true;
    setAsking(true);
    setAskError('');
    setAskFailure(null);
    setQuestion('');
    try {
      const response = await axiosInstance.post(askEndpoint, {
        question: submittedQuestion,
        prior_turns: priorTurns,
      }, { timeout: 35000 });
      const answer = response.data?.answer;
      if (!answer) throw new Error('empty Ask Pinngro response');
      setConversation((current) => [...current, { role: 'user', content: submittedQuestion }, { role: 'assistant', content: answer }]);
      setStarterQuestionsOpen(false);
    } catch (requestError) {
      const detail = requestError?.response?.data?.detail;
      const message = (typeof detail === 'string' ? detail : detail?.message) || 'Ask Pinngro is temporarily unavailable. The recorded trade explanation above is still available.';
      setAskError(message);
      setAskFailure({
        question: submittedQuestion,
        priorTurns,
        retryable: Boolean(detail && typeof detail === 'object' && detail.retryable),
        retry_after_seconds: Number(detail && typeof detail === 'object' ? detail.retry_after_seconds : 0) || 0,
      });
    } finally {
      askingRef.current = false;
      setAsking(false);
    }
  };

  const retryAsk = () => {
    if (!askFailure || retryRemaining > 0 || !askFailure.retryable) return;
    ask(askFailure.question, askFailure.priorTurns);
  };

  const retryLabel = retryRemaining > 0
    ? `Try again in about ${Math.ceil(retryRemaining / 60)} minute${Math.ceil(retryRemaining / 60) === 1 ? '' : 's'}`
    : 'Try again';

  const clearConversation = () => {
    // Conversation turns are intentionally browser-only in v1. Clearing them
    // has no server-side effect and does not alter retained usage evidence.
    setConversation([]);
    setQuestion('');
    setAskError('');
    setAskFailure(null);
    setRetryRemaining(0);
    setStarterQuestionsOpen(true);
    previousConversationLengthRef.current = 0;
  };

  const starterQuestions = [
    'Why did I lose money?',
    "Why didn't the stop-loss trigger?",
    'What is a whipsaw?',
    'Which rule caused the exit?',
    ...(explanation?.subject?.type === 'completed_trade' ? ['Would Trend Confirmation have taken this trade?'] : []),
  ];

  const label = proposalId ? 'Why this proposal?' : fillId ? 'Explain this trade' : 'Explain position';
  return (
    <section className={`rounded-xl border border-blue-200 bg-blue-50/60 dark:border-blue-900 dark:bg-blue-950/20 ${compact ? 'mt-3 p-3' : 'mt-4 p-4'}`}>
      <button type="button" onClick={toggle} aria-expanded={open} className="flex w-full items-center justify-between gap-3 text-left font-black text-blue-950 dark:text-blue-100">
        <span className="inline-flex items-center gap-2"><FaLightbulb /> {label}</span>
        <FaChevronDown className={open ? 'rotate-180 transition-transform' : 'transition-transform'} />
      </button>
      {!open && <p className="mt-1 text-xs leading-5 text-slate-600 dark:text-slate-300">See the recorded strategy evidence, approval path, Practice fill, and outcome.</p>}
      {open && <div className="mt-3 space-y-3">
        {loading && <p className="text-sm text-slate-600 dark:text-slate-300">Loading recorded evidence…</p>}
        {error && <p role="alert" className="text-sm font-semibold text-rose-800 dark:text-rose-200">{error}</p>}
        {explanation?.sections?.map((section) => (
          <details key={section.key} open={section.key === 'entry'} className="rounded-lg bg-white/80 p-3 dark:bg-slate-900/70">
            <summary className="cursor-pointer font-bold text-slate-900 dark:text-white">{section.title}</summary>
            <p className="mt-2 text-sm leading-6 text-slate-700 dark:text-slate-300">{section.summary}</p>
            {section.facts?.length > 0 && <ul className="mt-2 list-disc space-y-1 pl-5 text-sm leading-6 text-slate-700 dark:text-slate-300">{section.facts.map((fact) => <li key={fact}>{fact}</li>)}</ul>}
            {section.technical_facts?.length > 0 && <details className="mt-3 rounded-lg bg-slate-50 p-3 dark:bg-slate-800/70">
              <summary className="cursor-pointer text-sm font-bold text-slate-700 dark:text-slate-200">Recorded values and thresholds</summary>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-xs leading-5 text-slate-600 dark:text-slate-300">{section.technical_facts.map((fact) => <li key={fact}>{fact}</li>)}</ul>
            </details>}
          </details>
        ))}
        {explanation?.availability?.map((item) => <p key={item.code} className="rounded-lg bg-amber-50 p-3 text-sm text-amber-950 dark:bg-amber-950/40 dark:text-amber-100">{item.message}</p>)}
        {explanation?.subject?.type === 'completed_trade' && comparisonRoot && <section className="rounded-lg border border-violet-200 bg-white/80 p-3 dark:border-violet-900 dark:bg-slate-900/70" aria-label="Compare with another strategy">
          <button type="button" onClick={toggleComparison} aria-expanded={comparisonOpen} className="flex w-full items-center justify-between gap-3 text-left font-bold text-slate-900 dark:text-white">
            <span>Compare with another strategy</span><FaChevronDown className={comparisonOpen ? 'rotate-180 transition-transform' : 'transition-transform'} />
          </button>
          {!comparisonOpen && <p className="mt-1 text-xs leading-5 text-slate-600 dark:text-slate-300">See whether another immutable Pinngro strategy would have signalled on this same completed market session.</p>}
          {comparisonOpen && <div className="mt-3 space-y-3">
            {comparing && <p className="text-sm text-slate-600 dark:text-slate-300">Comparing the recorded market input…</p>}
            {comparisonError && <p role="alert" className="rounded-lg bg-amber-50 p-3 text-sm font-semibold text-amber-950 dark:bg-amber-950/40 dark:text-amber-100">{comparisonError}</p>}
            {comparisonOptions?.options?.length > 0 && <div className="flex flex-wrap gap-2">{comparisonOptions.options.map((option) => <button key={option.id} type="button" disabled={comparing} onClick={() => compareStrategy(option.id)} className="rounded-full border border-violet-300 px-3 py-1.5 text-left text-xs font-semibold text-violet-900 disabled:opacity-60 dark:border-violet-800 dark:text-violet-100">{strategyDisplayName(null, option.name)}{option.practice_only ? ' · Practice only' : ''}</button>)}</div>}
            {comparisonOptions && !comparisonOptions.options?.length && <p className="text-sm text-slate-600 dark:text-slate-300">{comparisonOptions.message || 'No compatible immutable strategy is available for this recorded entry.'}</p>}
            {comparison && <div className="rounded-lg bg-violet-50 p-3 text-sm leading-6 text-slate-800 dark:bg-violet-950/30 dark:text-slate-100">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">Original strategy</p>
              <p className="font-bold">{strategyDisplayName(null, comparison.original_strategy?.name)}</p>
              <p className="mt-2 text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">Compared strategy</p>
              <p className="font-bold">{strategyDisplayName(null, comparison.comparison_strategy?.name)}{comparison.comparison_strategy?.practice_only ? ' · Practice only' : ''}</p>
              {comparison.comparison_signal?.status === 'would_have_produced_entry_signal' ? <p className="mt-1">Would have produced an entry signal on the same completed market session.</p> : comparison.comparison_signal?.status === 'would_not_have_produced_entry_signal' ? <p className="mt-1">Would not have produced an entry signal on the same completed market session.</p> : <p className="mt-1">{comparison.message || "Pinngro's recorded evidence does not establish this."}</p>}
              {comparison.differing_rules?.length > 0 && <ul className="mt-2 list-disc space-y-1 pl-5 text-xs leading-5">{comparison.differing_rules.map((rule) => <li key={rule.rule_id}>{rule.description}</li>)}</ul>}
              <p className="mt-3 text-xs text-slate-600 dark:text-slate-300">{comparison.disclaimer}</p>
              {(comparison.comparison_strategy?.id || selectedComparisonId) && <div className="mt-3 flex flex-wrap gap-2"><Link to={`/settings?tab=trading&deriveFrom=${encodeURIComponent(comparison.comparison_strategy?.id || selectedComparisonId)}`} className="rounded-lg border border-violet-300 px-3 py-2 text-xs font-bold text-violet-900 dark:border-violet-800 dark:text-violet-100">Create my version</Link><Link to="/settings?tab=trading" className="rounded-lg border border-slate-300 px-3 py-2 text-xs font-bold text-slate-700 dark:border-slate-700 dark:text-slate-200">View strategy workshop</Link></div>}
            </div>}
          </div>}
        </section>}
        {explanation && <section className="rounded-lg border border-blue-200 bg-white/80 p-3 dark:border-blue-900 dark:bg-slate-900/70" aria-label="Ask Pinngro about this trade">
          <div className="flex items-start justify-between gap-3">
            <h3 className="font-bold text-slate-900 dark:text-white">Ask Pinngro about this trade</h3>
            {conversation.length > 0 && <button type="button" onClick={clearConversation} disabled={asking} className="shrink-0 text-xs font-semibold text-blue-800 underline underline-offset-2 disabled:opacity-60 dark:text-blue-200">Clear conversation</button>}
          </div>
          <p className="mt-1 text-xs leading-5 text-slate-600 dark:text-slate-300">Answers about this trade use Pinngro&apos;s recorded evidence. General definitions are educational and are not trading recommendations.</p>
          {conversation.length > 0 && <button type="button" onClick={() => setStarterQuestionsOpen((current) => !current)} className="mt-3 text-xs font-semibold text-blue-800 underline underline-offset-2 dark:text-blue-200">{starterQuestionsOpen ? 'Hide starter questions' : 'Show starter questions'}</button>}
          {(conversation.length === 0 || starterQuestionsOpen) && <div className="mt-3 flex flex-wrap gap-2" data-testid="ask-pinngro-starters">{starterQuestions.map((starter) => <button key={starter} type="button" disabled={asking} onClick={() => ask(starter)} className="rounded-full border border-blue-200 px-3 py-1.5 text-left text-xs font-semibold text-blue-800 disabled:opacity-60 dark:border-blue-800 dark:text-blue-200">{starter}</button>)}</div>}
          {conversation.length > 0 && <div ref={conversationPanelRef} className="mt-3 max-h-[65svh] space-y-3 overflow-y-auto overscroll-contain pr-1 sm:max-h-[30rem]" aria-live="polite" data-testid="ask-pinngro-conversation">{conversation.map((turn, index) => <div key={`${turn.role}-${index}`} className={turn.role === 'user' ? 'min-w-0 rounded-lg border border-slate-200 bg-slate-100 p-3 text-sm text-slate-800 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100' : 'min-w-0 rounded-lg border border-blue-100 bg-blue-50 p-3 text-sm leading-6 text-slate-800 dark:border-blue-900 dark:bg-blue-950/40 dark:text-slate-100'}><strong>{turn.role === 'user' ? 'You' : 'Pinngro'}</strong><p className="mt-1 break-words whitespace-pre-wrap">{turn.content}</p></div>)}</div>}
          <div className="sticky bottom-0 z-10 mt-3 border-t border-blue-100 bg-white/95 pt-3 dark:border-blue-900 dark:bg-slate-900/95">
            {askError && <div role="alert" className="mb-3 rounded-lg bg-amber-50 p-3 text-sm font-semibold text-amber-950 dark:bg-amber-950/40 dark:text-amber-100" data-testid="ask-pinngro-error"><p>{askError}</p>{askFailure?.retryable && <button type="button" onClick={retryAsk} disabled={asking || retryRemaining > 0} className="mt-2 rounded-lg border border-amber-400 px-3 py-1.5 text-sm font-bold disabled:opacity-60 dark:border-amber-600" data-testid="ask-pinngro-retry">{retryLabel}</button>}</div>}
            <form className="flex items-end gap-2" onSubmit={(event) => { event.preventDefault(); ask(); }}>
              <label className="sr-only" htmlFor={`ask-pinngro-${proposalId || fillId || `${botId}-${symbol}`}`}>Ask Pinngro about this trade</label>
              <textarea id={`ask-pinngro-${proposalId || fillId || `${botId}-${symbol}`}`} value={question} maxLength={1000} rows={2} onChange={(event) => setQuestion(event.target.value)} placeholder="Ask about this recorded trade" className="min-w-0 flex-1 rounded-lg border border-slate-300 bg-white p-2 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-800 dark:text-white" />
              <button type="submit" disabled={asking || !question.trim()} className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-blue-700 px-3 py-2 text-sm font-bold text-white disabled:opacity-60">{asking ? 'Asking…' : <><FaPaperPlane /> Ask</>}</button>
            </form>
          </div>
        </section>}
      </div>}
    </section>
  );
};

export default TradeExplanation;
